import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Play, Eye, Clock, Heart, Upload, Loader2,
  AlertCircle, Share2, ArrowLeft, User, Users, Calendar,
  RefreshCw, XCircle, CheckCircle, FileWarning
} from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';

export default function LiveReplayPage() {
  const { replayId } = useParams();
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchReplay(); }, [replayId]);

  // Auto-poll when processing
  useEffect(() => {
    if (replay?.recording_status !== 'processing') return;
    const interval = setInterval(fetchReplay, 5000);
    return () => clearInterval(interval);
  }, [replay?.recording_status]);

  const fetchReplay = async () => {
    try {
      const res = await fetch(`${API}/api/live/replays/${replayId}`);
      if (res.ok) {
        const data = await res.json();
        setReplay(data.replay);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please select a video file'); return; }
    if (file.size > 500 * 1024 * 1024) { toast.error('File too large (max 500MB)'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/live/replays/${replayId}/upload-video`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Replay video uploaded!');
        setReplay(prev => ({ ...prev, video_url: data.video_url, recording_status: 'ready', error_message: null }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await fetch(`${API}/api/live/replays/${replayId}/retry`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.recording_status === 'ready') {
          toast.success('Replay recovered!');
          setReplay(prev => ({ ...prev, video_url: data.video_url, recording_status: 'ready', error_message: null }));
        } else if (data.recording_status === 'processing') {
          toast.info('Re-processing replay...');
          setReplay(prev => ({ ...prev, recording_status: 'processing', error_message: null }));
        } else {
          toast.error(data.message || 'Retry failed');
          setReplay(prev => ({ ...prev, recording_status: 'failed', error_message: data.message }));
        }
      } else {
        toast.error('Retry failed');
      }
    } catch { toast.error('Network error'); }
    finally { setRetrying(false); }
  };

  const isOwner = user?.user_id === replay?.user_id;
  const status = replay?.recording_status || 'failed';
  const isReady = status === 'ready' && replay?.video_url;
  const isProcessing = status === 'processing';
  const isFailed = status === 'failed' || (!replay?.video_url && !isProcessing);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  if (!replay) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Replay Not Found</h2>
          <p className="text-muted-foreground mb-4">This replay does not exist.</p>
          <Link to="/live"><Button>Back to Live</Button></Link>
        </Card>
      </div>
    );
  }

  const formatDuration = (s) => {
    if (!s) return '--:--';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen py-6" data-testid="live-replay-page">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/live" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Live
        </Link>

        {/* Video Player / Status Area */}
        <div className="mb-6">
          {isReady ? (
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video" data-testid="replay-video-player">
              <video
                src={replay.video_url.startsWith('http') ? replay.video_url : `${API}${replay.video_url}`}
                controls
                playsInline
                className="w-full h-full"
                poster={replay.cover_image}
                crossOrigin="anonymous"
              />
            </div>
          ) : isProcessing ? (
            <Card className="aspect-video flex items-center justify-center bg-muted/30 rounded-xl" data-testid="replay-processing">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-orange-400 mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-1">Replay Processing...</h3>
                <p className="text-sm text-muted-foreground">Your recording is being finalized. This page auto-refreshes.</p>
                <Badge className="mt-3 bg-yellow-500/20 text-yellow-400">Processing</Badge>
              </div>
            </Card>
          ) : (
            <Card className="aspect-video flex items-center justify-center bg-muted/30 rounded-xl" data-testid="replay-failed">
              <div className="text-center max-w-sm">
                <XCircle className="w-12 h-12 text-red-400/50 mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-1">Replay Failed to Save</h3>
                {replay.error_message && (
                  <p className="text-sm text-red-400/80 mb-3 bg-red-500/10 rounded-lg p-2" data-testid="replay-error-message">
                    {replay.error_message}
                  </p>
                )}
                {isOwner && (
                  <div className="space-y-2">
                    <Button
                      onClick={handleRetry}
                      disabled={retrying}
                      variant="outline"
                      className="w-full"
                      data-testid="retry-replay-btn"
                    >
                      {retrying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Retrying...</> : <><RefreshCw className="w-4 h-4 mr-2" />Retry Save</>}
                    </Button>
                    <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleUploadVideo} data-testid="replay-file-input" />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      data-testid="upload-replay-btn"
                    >
                      {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload Video</>}
                    </Button>
                    <p className="text-xs text-muted-foreground">MP4, WebM, or MOV up to 500MB</p>
                  </div>
                )}
                {!isOwner && (
                  <p className="text-sm text-muted-foreground">The creator hasn't uploaded the replay video yet.</p>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Replay Info */}
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-2" data-testid="replay-title">{replay.title}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 flex-wrap">
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{replay.views || 0} views</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDuration(replay.duration_seconds)}</span>
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{replay.created_at ? new Date(replay.created_at).toLocaleDateString() : '--'}</span>
              {replay.viewer_count > 0 && <span className="flex items-center gap-1"><Users className="w-4 h-4" />{replay.viewer_count} peak</span>}
            </div>

            <Link to={replay.author_handle ? `/u/${replay.author_handle}` : '#'} className="flex items-center gap-3 mb-4 hover:opacity-80">
              <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center overflow-hidden">
                {replay.author_avatar ? <img src={replay.author_avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div>
                <p className="font-medium">{replay.author_name || 'Creator'}</p>
                {replay.author_handle && <p className="text-xs text-muted-foreground">@{replay.author_handle}</p>}
              </div>
            </Link>

            {replay.description && <p className="text-muted-foreground text-sm mb-3">{replay.description}</p>}

            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {isReady && <Badge className="bg-green-500/20 text-green-400" data-testid="status-badge-ready"><CheckCircle className="w-3 h-3 mr-1" />Ready</Badge>}
              {isProcessing && <Badge className="bg-yellow-500/20 text-yellow-400" data-testid="status-badge-processing"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>}
              {isFailed && <Badge className="bg-red-500/20 text-red-400" data-testid="status-badge-failed"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>}
            </div>

            {/* Debug logs (owner only) */}
            {isOwner && replay.replay_logs?.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  <FileWarning className="w-3 h-3 inline mr-1" />View replay logs ({replay.replay_logs.length} entries)
                </summary>
                <div className="mt-2 p-3 bg-muted/30 rounded-lg text-xs font-mono space-y-1 max-h-40 overflow-y-auto" data-testid="replay-logs">
                  {replay.replay_logs.map((log, i) => <div key={i} className="text-muted-foreground">{log}</div>)}
                </div>
              </details>
            )}
          </div>

          {/* Actions */}
          <div className="w-full md:w-48 space-y-2">
            {isReady && (
              <Button variant="outline" className="w-full" onClick={() => {
                try {
                  if (navigator.share) {
                    navigator.share({ title: replay.title, url: window.location.href }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(window.location.href).catch(() => {});
                  }
                } catch {}
                toast.success('Link copied!');
              }}>
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            )}
            {isOwner && isReady && (
              <>
                <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleUploadVideo} />
                <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-2" /> Re-upload
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
